















import { useFeatureFlags, useFilters } from '@/app/composable';
import store from '@/app/store';
import { PencilAltIcon, TrashIcon } from '@vue-hero-icons/outline';
import { PropType, Ref, computed, defineComponent } from '@vue/composition-api';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import * as R from 'ramda';
import { useAssetRetrieval } from '../composable/asset-retrieval';
import { useAssetStatus } from '../composable/asset-status';
import { AssetType, AssetTypeId, ModelSourceOptions, StatusCode } from '../constants';
import { Asset } from '../types';
import { CardLayoutCard } from '@/app/components';
dayjs.extend(utc);
dayjs.extend(minMax);

export default defineComponent({
    name: 'AssetOverview',
    components: {
        CardLayoutCard,
    },
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        filter: {
            type: String,
            required: true,
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
    },
    setup(props, { emit, root }) {
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');

        const user = computed(() => store.state.auth.user);
        const isAssetCreator = computed(() => user.value && user.value.id === props.asset.createdBy.id);
        const model = computed(() => props.asset && props.asset.assetTypeId === AssetTypeId.Model);

        const updatedDate = computed(() =>
            props.asset.modifiedAt
                ? dayjs.max([dayjs(props.asset.modifiedAt), dayjs(props.asset.updatedAt)]).toString()
                : props.asset.updatedAt,
        );

        const { formatDateTime } = useFilters();

        const assetType = computed(() => {
            switch (props.asset.assetTypeId) {
                case AssetTypeId.Model:
                    return {
                        type: AssetType.Model,
                        class: 'text-cherry-700',
                    };
                case AssetTypeId.Result:
                    return {
                        type: AssetType.Result,
                        class: 'text-blue-800',
                    };
                default:
                    return {
                        type: AssetType.Dataset,
                        class: 'text-orange-800',
                    };
            }
        });

        const allFilterEnabled = computed(() => props.filter === 'all');

        const status = computed(() => props.asset?.status);
        const asset: Ref<Asset> = computed<Asset>(() => props.asset);
        const { label: assetStatusLabel, colour: statusClass } = useAssetStatus(status);
        const {
            isRetrievalDisabled,
            retrievalTooltip,
            accessibilityKey,
            belongsToUserOrOrganisation,
        } = useAssetRetrieval(asset, user);

        const isModel = computed(() => asset.value?.assetTypeId === AssetTypeId.Model);
        const isUploadedModel = computed(
            () => isModel.value && asset.value?.metadata?.model.source === ModelSourceOptions.uploaded,
        );

        const provenanceLinkTooltip = computed(() => {
            if (isUploadedModel.value) return 'The asset was uploaded by the User';

            const pipelineType = props.asset.assetTypeId === AssetTypeId.Dataset ? 'Data Check-in' : 'Analytics';
            if (props.asset.provenanceAssetWorkflowIds?.length === 0)
                return `The ${pipelineType} Pipeline, which created this asset, has been deleted.`;

            return `Navigate to ${pipelineType} Pipeline`;
        });

        const retrieve = () => {
            if (accessibilityKey.value)
                root.$router.push({
                    name: 'retrieval:create',
                    params: {
                        assetId: props.asset.id,
                        accessibility: accessibilityKey.value,
                    } as any,
                });
        };

        const actions = computed(() => {
            const actionsArray = [];
            if (
                asset.value.status !== StatusCode.Deleted &&
                isAssetCreator.value &&
                (!model.value || (model.value && asset.value.status !== StatusCode.Archived))
            ) {
                actionsArray.push({
                    id: '1',
                    label: 'Edit',
                    icon: PencilAltIcon,
                    click: () => emit('edit', asset.value),
                });
            

                if (!model.value || (model.value && asset.value.status !== StatusCode.Archived)) {
                    actionsArray.push({
                        id: '4',
                        label: 'Delete',
                        icon: TrashIcon,
                        tooltip: 'Delete asset',
                        click: () => emit('destroy', asset.value),
                    });
                }
            }

            return actionsArray;
        });

        const statusMap = {
            [StatusCode.Available]: {
                label: 'Available',
                background: 'bg-green-800',
                text: 'text-green-100',
            },
            [StatusCode.Draft]: {
                label: 'Draft',
                background: 'bg-blue-800',
                text: 'text-blue-100',
            },
            [StatusCode.Archived]: {
                label: 'Deprecated',
                background: 'bg-gray-600',
                text: 'text-gray-100',
            },
            [StatusCode.Deleted]: {
                label: 'Unavailable',
                background: 'bg-red-800',
                text: 'text-red-100',
            },
            [StatusCode.Uploading]: {
                label: 'Uploading',
                background: 'bg-blue-800',
                text: 'text-blue-100',
            },
            [StatusCode.Incomplete]: {
                label: 'Incomplete',
                background: 'bg-purple-500',
                text: 'text-purple-100',
            },
            [StatusCode.Updating]: {
                label: 'Updating',
                background: 'bg-orange-800',
                text: 'text-orange-100',
            },
        };

        return {
            dayjs,
            emit,
            statusClass,
            isAlertsEnabled,
            assetStatusLabel,
            user,
            model,
            StatusCode,
            status,
            isAssetCreator,
            allFilterEnabled,
            AssetType,
            assetType,
            provenanceLinkTooltip,
            R,
            retrieve,
            retrievalTooltip,
            isRetrievalDisabled,
            belongsToUserOrOrganisation,
            formatDateTime,
            updatedDate,
            accessibilityKey,
            isUploadedModel,
            actions,
            statusMap,
        };
    },
});
